import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Header from './components/Header.jsx';
import NavBar from './components/NavBar.jsx';
import HomeScreen from './components/HomeScreen.jsx';
import LandingScreen from './components/LandingScreen.jsx';
import Flashcards from './components/Flashcards.jsx';
import PracticeScreen from './components/PracticeScreen.jsx';
import PracticeRunner from './components/PracticeRunner.jsx';
import DailyChallenge from './components/DailyChallenge.jsx';
import BeforeAfter from './components/BeforeAfter.jsx';
import ToScale from './components/ToScale.jsx';
import ThirdDay from './components/ThirdDay.jsx';
import TwentyFaces from './components/TwentyFaces.jsx';
import VerseRecall from './components/VerseRecall.jsx';
import CharactersScreen from './components/CharactersScreen.jsx';
import StoryScreen from './components/StoryScreen.jsx';
import StoryChapter from './components/StoryChapter.jsx';
import PathsScreen from './components/PathsScreen.jsx';
import VerseListScreen from './components/VerseListScreen.jsx';
import ChatScreen from './components/ChatScreen.jsx';
import CharacterQuiz from './components/CharacterQuiz.jsx';
import PaywallModal from './components/PaywallModal.jsx';
import AccountModal from './components/AccountModal.jsx';
import Ambience from './components/Ambience.jsx';
import ConfirmBanner from './components/ConfirmBanner.jsx';
import HymnDock from './components/HymnDock.jsx';
import Toasts from './components/Toasts.jsx';

import { useProgress } from './hooks/useProgress.js';
import { useAccount } from './hooks/useAccount.js';
import { useAmbientAudio } from './hooks/useAmbientAudio.js';
import { useHymnal } from './hooks/useHymnal.js';

import { VERSES, VERSES_BY_ID, versesForBook } from './data/verses.js';
import { CHAPTERS_BY_ID } from './data/story.js';
import { QUESTIONS } from './data/questions.js';
import { buildPracticeSet } from './lib/practice.js';
import { FREE_LIMITS, limitDeck, lockedCount } from './lib/tiers.js';
import { toDayKey } from './lib/streak.js';
import { setMuted as setSfxMuted } from './lib/sfx.js';
import { billingConfig } from './lib/accountClient.js';
import {
  HOME_TAB,
  isQuizPath,
  pathForState,
  practiceModeForPath,
  tabForPath,
} from './lib/routes.js';
import { XP_REWARDS } from './lib/xp.js';

const PRACTICE_TITLES = {
  quiz: 'Quick quiz',
  timed: 'Beat the clock',
  weak: 'Weak spots',
};

/** Deterministic verse of the day — same for the whole calendar day. */
function verseOfTheDay(dayKey) {
  let hash = 0;
  for (let i = 0; i < dayKey.length; i++) {
    hash = (hash * 31 + dayKey.charCodeAt(i)) >>> 0;
  }
  return VERSES[hash % VERSES.length];
}

export default function App() {
  const progress = useProgress();
  const { state, level, stats } = progress;
  const account = useAccount();

  /**
   * Entitlement, from the server or not at all.
   *
   * This used to read `state.premium` — a boolean in localStorage that anyone
   * could set from the console. It is now whatever the account says, and an
   * account is the only way to have one.
   */
  const isPremium = account.isPremium;

  // Which section is open, read from the address so a link, a bookmark or the
  // back button all land where they say they will. See lib/routes.js.
  const [tab, setTab] = useState(
    () => tabForPath(typeof window === 'undefined' ? '/' : window.location.pathname) ?? HOME_TAB,
  );
  // null = the Practice hub. Read from the address so a promoted game opens
  // directly instead of landing people in the app somewhere near it.
  const [practiceMode, setPracticeMode] = useState(() =>
    typeof window === 'undefined' ? null : practiceModeForPath(window.location.pathname),
  );
  const [deck, setDeck] = useState({ type: 'all' });
  const [paywall, setPaywall] = useState(null); // { reason } | null
  const [chatVerse, setChatVerse] = useState(null);
  const [savedMode, setSavedMode] = useState('bookmarks');
  const [openChapter, setOpenChapter] = useState(null); // chapterId | null
  const [accountPrompt, setAccountPrompt] = useState(null); // { mode, reason } | null

  /*
   * Trial terms, read from the server rather than written here.
   *
   * The landing page states the length before there is any account to ask, so
   * this cannot come off the user row. It is also why the home upsell no
   * longer hardcodes "3 days" — it said exactly that while the trial was
   * being changed to seven, which is the kind of drift that only ever gets
   * noticed by a customer.
   */
  const [trial, setTrial] = useState({ days: 7, available: false });
  useEffect(() => {
    let live = true;
    billingConfig()
      .then(
        (config) =>
          live &&
          setTrial({ days: config.trialDays ?? 7, available: !!config.trialAvailable }),
      )
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  const trialDays = trial.days;

  /*
   * Whether to offer the trial, for whoever is looking.
   *
   * Signed in, the account row is authoritative — it knows if this person has
   * already had one. Signed out there is no row, and reading `account.user`
   * gave `undefined`, so every anonymous visitor was shown "Open the whole
   * library" instead of the free trial. That is the weaker line, shown to
   * exactly the people the trial exists to convert.
   */
  const trialAvailable = account.signedIn
    ? !!account.user?.trialAvailable
    : trial.available;

  /*
   * Dismissing the pitch lasts for this visit, not forever.
   *
   * It was permanent, which meant somebody who tapped "look around first",
   * left, and came back later was never asked again. sessionStorage is exactly
   * the right lifetime: it never nags during a visit, and a genuine return
   * visit gets the offer once more.
   */
  const [landingDismissed, setLandingDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('sj:landing-dismissed') === '1';
    } catch {
      return false; // private mode can throw on access alone
    }
  });
  const dismissLanding = useCallback(() => {
    try {
      sessionStorage.setItem('sj:landing-dismissed', '1');
    } catch {
      /* still dismissed for this render, just not remembered */
    }
    setLandingDismissed(true);
  }, []);

  /**
   * The character quiz, which is the one thing here with its own address.
   *
   * It exists to be sent to people, so a link has to open it directly rather
   * than landing on the home screen and asking them to go looking. The server
   * serves index.html for any non-API path, so /quiz reaches the app and this
   * reads it off the URL on the way in.
   *
   * Opening and closing push history, so the phone's back gesture leaves the
   * quiz instead of leaving the site — which is what it would otherwise do to
   * someone who arrived on that link with no history behind them.
   */
  const [quizOpen, setQuizOpen] = useState(
    () => typeof window !== 'undefined' && isQuizPath(window.location.pathname),
  );

  /**
   * Back and forward. One handler for both the quiz and the tabs, because they
   * are two halves of the same question — what does this address show?
   */
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      setQuizOpen(isQuizPath(path));
      setTab(tabForPath(path) ?? HOME_TAB);
      setPracticeMode(practiceModeForPath(path));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /**
   * The address follows the screen.
   *
   * Done here rather than inside each navigation handler so there is one rule
   * instead of eight, and so screens reached indirectly — the review deck, a
   * verse sent to the chat, the paths browser — get an address too without
   * every caller having to remember.
   *
   * The comparison is what keeps this from fighting the back button: after a
   * popstate the address already matches, so nothing is pushed and the entry
   * we just came from is not immediately re-added.
   */
  const firstRoute = useRef(true);
  useEffect(() => {
    const want = pathForState({ tab, quizOpen, practiceMode });
    if (window.location.pathname === want) {
      firstRoute.current = false;
      return;
    }
    // An unrecognised address on arrival is corrected in place. Pushing would
    // leave the typo in history, so Back would take them straight back to it.
    if (firstRoute.current) window.history.replaceState({}, '', want);
    else window.history.pushState({}, '', want);
    firstRoute.current = false;
  }, [tab, quizOpen, practiceMode]);

  const openQuiz = useCallback(() => {
    setQuizOpen(true);
    window.scrollTo({ top: 0 });
  }, []);

  const closeQuiz = useCallback(() => setQuizOpen(false), []);
  /**
   * A reset token arrives as `?reset=…` from the email.
   *
   * Read once, during the first render, and immediately scrubbed from the
   * address bar: it is a bearer credential, and leaving it in the URL puts it
   * in history, in the Referer of anything the page loads, and in whatever the
   * user pastes when they ask a friend why the link isn't working.
   */
  const [resetToken] = useState(() => {
    const token = new URLSearchParams(window.location.search).get('reset');
    if (token) window.history.replaceState({}, '', window.location.pathname);
    return token;
  });
  const [resetDone, setResetDone] = useState(false);
  /**
   * The confirmation link lands back here as `?confirmed=…`.
   *
   * Read once and scrubbed from the address bar, the same as the reset token —
   * not because it is a secret (the server already spent it) but because a
   * reload should not re-announce something that happened minutes ago.
   */
  const [confirmOutcome] = useState(() => {
    const outcome = new URLSearchParams(window.location.search).get('confirmed');
    if (outcome) window.history.replaceState({}, '', window.location.pathname);
    return outcome;
  });
  const [confirmDismissed, setConfirmDismissed] = useState(false);
  /** How a round trip to Google went, read once and scrubbed like the rest. */
  const [signinOutcome] = useState(() => {
    const outcome = new URLSearchParams(window.location.search).get('signin');
    if (outcome) window.history.replaceState({}, '', window.location.pathname);
    return outcome;
  });
  /**
   * Whether the server can actually send email.
   *
   * Asked once, and it decides whether the "confirm your email" strip appears
   * at all. Nagging somebody to click a link while the deployment is printing
   * its mail into a log file would be asking them to wait for something that is
   * never going to arrive. Defaults to false, so the quiet failure is a missing
   * prompt rather than an impossible one.
   */
  const [emailWorks, setEmailWorks] = useState(false);
  useEffect(() => {
    let live = true;
    fetch('/api/health?json', { credentials: 'omit' })
      .then((response) => (response.ok ? response.json() : null))
      .then((health) => {
        if (live && health?.email === 'sending') setEmailWorks(true);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  // Bumped on each run so leaving and re-entering builds a fresh set from
  // whatever is still unlearned, rather than replaying the same questions.
  const [practiceRun, setPracticeRun] = useState(0);

  const audio = useAmbientAudio(state.settings.ambient);

  /**
   * The hymns. Separate from the ambient chant bed, which stays as it was —
   * one is music you choose, the other is atmosphere you forget about.
   */
  const hymnal = useHymnal({ level: state.settings.musicLevel ?? 0.7 });

  // Push the persisted music level into the pad, which lives outside React.
  useEffect(() => {
    audio.setLevel(state.settings.musicLevel ?? 0.7);
  }, [audio, state.settings.musicLevel]);

  /**
   * Persist the audio preference only when the user actually toggles it.
   * Syncing it from `audio.enabled` in an effect would overwrite the saved
   * preference with `false` on every load, since autoplay policy means audio
   * can never be on until the user taps.
   */
  const toggleAudio = useCallback(() => {
    const next = !audio.enabled;
    audio.toggle();
    progress.setSetting('ambient', next);
  }, [audio, progress]);

  // Keep the sound module in step with the persisted preference. It lives
  // outside React so it can be called from anywhere without prop-drilling.
  useEffect(() => {
    setSfxMuted(!state.settings.sfx);
  }, [state.settings.sfx]);

  /**
   * Pull the account's saved progress once on sign-in, then mirror local
   * changes up.
   *
   * The merge is deliberately "whichever is further along wins", per field
   * rather than whole-payload: someone who studied on their phone offline and
   * then signs in on a laptop should not lose either side. XP and streaks only
   * ever move forward, so taking the maximum is both simple and right.
   */
  const pulled = useRef(false);
  const { signedIn, pullDown, syncUp } = account;
  const userId = account.user?.id ?? null;
  const { adoptAccount } = progress;

  useEffect(() => {
    if (!signedIn || pulled.current) return;
    pulled.current = true;
    pullDown().then((remote) => adoptAccount(userId, remote));
  }, [signedIn, userId, pullDown, adoptAccount]);

  useEffect(() => {
    if (!signedIn) {
      pulled.current = false;
      return undefined;
    }
    // Debounced: answering a question changes state several times in a second.
    const handle = setTimeout(() => syncUp(state), 1500);
    return () => clearTimeout(handle);
  }, [signedIn, syncUp, state]);

  /**
   * Coming back from Stripe.
   *
   * `?checkout=success` is a hint that a payment happened, and it is treated as
   * exactly that — a hint. It causes the app to *ask the server again*, on a
   * short poll, because the webhook that actually grants premium is racing this
   * redirect. Nothing here reads the entitlement from the URL, which is why
   * pasting this URL by hand achieves nothing.
   */
  const checkoutHandled = useRef(false);
  const { awaitPremium } = account;
  useEffect(() => {
    if (checkoutHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('checkout');
    if (!outcome) return;
    checkoutHandled.current = true;

    // Take it out of the address bar so a reload or a shared link doesn't
    // replay it — and so the session_id isn't left sitting in history.
    window.history.replaceState({}, '', window.location.pathname);

    if (outcome === 'cancelled') {
      progress.notify({
        tone: 'xp',
        title: 'Checkout cancelled',
        body: 'Nothing was charged. Premium is there whenever you want it.',
      });
      return;
    }
    if (outcome !== 'success') return;

    awaitPremium().then((granted) => {
      progress.notify(
        granted
          ? {
              tone: 'premium',
              title: 'Premium unlocked',
              body: 'Every verse, every question, every path.',
            }
          : {
              tone: 'xp',
              title: 'Payment received',
              body: 'Your subscription is still being confirmed — refresh in a moment.',
            },
      );
    });
  }, [awaitPremium, progress]);

  // Told once, on arrival back from the email.
  const confirmAnnounced = useRef(false);
  useEffect(() => {
    if (!confirmOutcome || confirmAnnounced.current) return;
    confirmAnnounced.current = true;
    account.refresh();
    progress.notify(
      confirmOutcome === '1'
        ? { tone: 'xp', title: 'Email confirmed', body: 'This account is yours for good now.' }
        : {
            tone: 'xp',
            title: 'That link has expired',
            body: 'Sign in and we will send you another.',
          },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmOutcome]);

  // A word about the trip to Google — including when there is nothing to say,
  // because somebody who backed out does not need telling they backed out.
  const signinAnnounced = useRef(false);
  useEffect(() => {
    if (!signinOutcome || signinAnnounced.current) return;
    signinAnnounced.current = true;
    if (signinOutcome === 'cancelled') return;

    if (signinOutcome === 'google') {
      account.refresh();
      return; // The header changing to their name says it better than a toast.
    }

    progress.notify({
      tone: 'xp',
      title: 'That did not work',
      body:
        signinOutcome === 'unverified'
          ? 'Google could not confirm that address. Try signing up with an email instead.'
          : 'Something went wrong signing in with Google. You can use an email and password instead.',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signinOutcome]);

  const today = toDayKey();
  const votd = useMemo(() => verseOfTheDay(today), [today]);

  const openPaywall = useCallback((reason) => setPaywall({ reason }), []);

  /**
   * What this person specifically has left to open.
   *
   * The generic "study without limits" is true of everybody and therefore
   * persuades nobody. Their own numbers are more convincing than any adjective,
   * and they are already on screen — this only repeats them at the moment it
   * matters.
   */
  const lockedReason = useCallback(() => {
    const chaptersLeft = stats.chaptersTotal - stats.chaptersRead;
    const versesLeft = stats.versesTotal - stats.known;
    const parts = [];
    if (chaptersLeft > 0) parts.push(`${chaptersLeft} chapters of the story`);
    if (versesLeft > 0) parts.push(`${versesLeft} verses`);
    if (!parts.length) return 'Study without limits, on every device you sign in on.';
    return `You have ${parts.join(' and ')} still ahead. Premium opens all of it.`;
  }, [stats]);

  /**
   * The one way tabs change. Leaving the story closes the open chapter, so
   * coming back lands on the map rather than dropping you into whatever you
   * last read.
   */
  const selectTab = useCallback((next) => {
    // The quiz renders over the top of whichever tab is behind it, so a tab
    // press while it is open changed the tab underneath and appeared to do
    // nothing at all. Closing it here is what makes the nav work from inside.
    setQuizOpen(false);
    // Leaving a section drops whatever was open inside it, so coming back lands
    // on that section's index rather than mid-chapter or mid-quiz. Tapping the
    // tab you are already on does the same thing — that is the one gesture
    // people reach for to get back out, and it used to do nothing at all.
    setTab((current) => {
      if (next !== 'story' || current === 'story') setOpenChapter(null);
      if (next !== 'practice' || current === 'practice') setPracticeMode(null);
      return next;
    });
  }, []);

  const startPractice = useCallback((mode) => {
    // Not a practice run: it has its own screen and its own address.
    if (mode === 'character') {
      openQuiz();
      return;
    }
    if (mode === 'paths') {
      setTab('paths');
      return;
    }
    if (mode === 'review') {
      setDeck({ type: 'review' });
      setTab('flashcards');
      return;
    }
    // setTab, not selectTab: selectTab clears practiceMode on the way into the
    // Practice tab, which is right when somebody presses the tab itself and
    // exactly wrong here, where the whole point is to arrive inside a game.
    //
    // Needed because this is now called from the home screen and from a direct
    // link, not only from the Practice hub where the tab was already correct.
    setTab('practice');
    setPracticeRun((n) => n + 1);
    setPracticeMode(mode);
  }, [openQuiz]);

  const startDeck = useCallback(
    (descriptor) => {
      setDeck(descriptor);
      selectTab('flashcards');
    },
    [selectTab],
  );

  const askAbout = useCallback(
    (verse) => {
      setChatVerse(verse);
      selectTab('chat');
    },
    [selectTab],
  );

  // ── Deck resolution ──────────────────────────────────────────────────────

  const { deckVerses, deckLocked, deckTitle, deckSubtitle } = useMemo(() => {
    if (deck.type === 'book') {
      const all = versesForBook(deck.book);
      return {
        deckVerses: limitDeck(all, isPremium),
        deckLocked: lockedCount(all, isPremium),
        deckTitle: deck.book,
        deckSubtitle: `${all.length} verse${all.length === 1 ? '' : 's'} in this path`,
      };
    }

    if (deck.type === 'bookmarks') {
      // Already user-curated, so no tier limit here.
      const saved = state.bookmarks.map((id) => VERSES_BY_ID[id]).filter(Boolean);
      return {
        deckVerses: saved,
        deckLocked: 0,
        deckTitle: 'Saved verses',
        deckSubtitle: 'Your bookmarks, as a deck',
      };
    }

    if (deck.type === 'review') {
      const review = VERSES.filter((v) => state.learning[v.id] === 'learning');
      return {
        deckVerses: review,
        deckLocked: 0,
        deckTitle: 'Review',
        deckSubtitle: 'Verses you marked “still learning”',
      };
    }

    return {
      deckVerses: limitDeck(VERSES, isPremium),
      deckLocked: lockedCount(VERSES, isPremium),
      deckTitle: 'All verses',
      deckSubtitle: isPremium
        ? `${VERSES.length} verses across both testaments`
        : `${FREE_LIMITS.flashcards} of ${VERSES.length} verses on the free tier`,
    };
  }, [deck, isPremium, state.bookmarks, state.learning]);

  // ── Quiz set ─────────────────────────────────────────────────────────────

  /**
   * The set for whichever mode is running. Rebuilt when the mode or the run
   * counter changes — deliberately not on every answer, which would reshuffle
   * the deck underneath the player mid-session.
   */
  const practiceQuestions = useMemo(() => {
    if (!practiceMode || practiceMode === 'recall') return [];
    const limit = isPremium ? 10 : FREE_LIMITS.quizQuestions;
    return buildPracticeSet({
      answers: state.quizAnswers,
      limit,
      seed: `${today}-${practiceMode}-${practiceRun}`,
      tiers: practiceMode === 'weak' ? ['weak'] : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceMode, practiceRun, isPremium, today]);

  const quizLocked = isPremium ? 0 : QUESTIONS.length - practiceQuestions.length;

  // ── Derived lists ────────────────────────────────────────────────────────

  const bookmarkedVerses = useMemo(
    () => state.bookmarks.map((id) => VERSES_BY_ID[id]).filter(Boolean),
    [state.bookmarks],
  );
  const reviewVerses = useMemo(
    () => VERSES.filter((v) => state.learning[v.id] === 'learning'),
    [state.learning],
  );

  const handleQuizFinish = useCallback(
    (correct, total) => {
      if (total > 0 && correct === total) {
        progress.addXp(XP_REWARDS.quizPerfectBonus, 'Perfect round');
      }
    },
    [progress],
  );

  /*
   * Who sees the pitch instead of the app.
   *
   * Anyone signed out who has not dismissed it, on the home tab.
   *
   * It used to also require that they had never done anything here, which
   * sounded careful and was the wrong rule: it meant anybody who had ever
   * touched the app anonymously — played the quiz once from a video, say —
   * never saw the offer again, and that is precisely the person most worth
   * making it to. Having used the thing is a reason to be sold to, not a
   * reason to be skipped.
   *
   * Two things keep it from being a wall. Dismissing is permanent, so it asks
   * once and never nags; and a deep link into /quiz or /before-or-after is
   * never intercepted, so a link that promised a playable thing still opens
   * the playable thing.
   */
  /*
   * What happens after somebody signs in or signs up.
   *
   * Signing up is hardly ever the errand — it is the step in front of one. The
   * landing page's button says "start your free days", so once there is an
   * account to attach a subscription to, the plans have to follow on their own.
   * Before this it simply closed and left people on the free tier, having
   * pressed a button that promised a trial and started nothing.
   */
  const finishPrompt = useCallback(
    (prompt) => {
      if (prompt?.then === 'trial') {
        openPaywall(`Pick a plan to start your ${trialDays} free days.`);
      }
    },
    [openPaywall, trialDays],
  );

  const showLanding = !account.signedIn && !landingDismissed && tab === 'home' && !quizOpen;

  /**
   * Tell Clarity which screen this is.
   *
   * Real URLs already give it something to group by; this is the label a human
   * reads when filtering recordings, and it costs one call. `clarity` is the
   * queue stub installed in index.html, so it exists long before the tag has
   * finished loading — but it is still optional, because an ad blocker is
   * entitled to remove it.
   *
   * The landing page is called out separately, and that matters more than the
   * rest put together. It shares an address with the home screen, so without
   * this there is no way to ask "how many people saw the pitch" — the question
   * had to be answered by comparing deploy timestamps against session times by
   * hand, which is arithmetic rather than measurement. Now it is a filter.
   *
   * Declared after showLanding on purpose: a dependency array is evaluated
   * during render, so naming a const declared further down leaves it in its
   * temporal dead zone and throws before the first paint. That exact mistake
   * blanked the whole app once already.
   */
  useEffect(() => {
    const screen = showLanding
      ? 'landing'
      : quizOpen
        ? 'quiz'
        : practiceMode
          ? `practice:${practiceMode}`
          : tab;
    window.clarity?.('set', 'screen', screen);
    // A second, coarser tag: did this person ever get pitched to at all? The
    // screen tag changes as they move, so on its own it cannot answer that.
    if (showLanding) window.clarity?.('set', 'sawPitch', 'yes');
  }, [showLanding, tab, quizOpen, practiceMode]);

  if (showLanding) {
    return (
      <div className="app app--landing" data-zone="landing">
        <Ambience zone="home" story={state.story} learning={state.learning} />
        <header className="landing-bar">
          <span className="landing-bar__brand">
            Scripture <em>Journey</em>
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setAccountPrompt({ mode: 'signin' })}
          >
            Sign in
          </button>
        </header>

        <main className="main main--landing">
          <LandingScreen
            trialDays={trialDays}
            onStart={() =>
              setAccountPrompt({
                mode: 'signup',
                reason: `Make an account, then start your ${trialDays} free days.`,
                // Carries on to the plans afterwards. Without this the button
                // said "start your free days", made an account, and dropped
                // people on the free tier having started nothing.
                then: 'trial',
              })
            }
            onExplore={dismissLanding}
          />
        </main>

        {accountPrompt ? (
          <AccountModal
            account={account}
            mode={accountPrompt.mode}
            reason={accountPrompt.reason}
            onClose={() => setAccountPrompt(null)}
            onAuthed={() => finishPrompt(accountPrompt)}
          />
        ) : null}
        {/*
          No Toasts here on purpose. The daily-visit bonus fires on mount, so a
          stranger being pitched to was getting "Welcome back — +15 XP for
          showing up today" floated over the benefits list: wrong words for
          somebody who has never been here, and covering the copy that is
          meant to be selling them the thing. The XP is still awarded; it is
          simply not announced to someone who has not arrived yet.
        */}
      </div>
    );
  }

  /*
   * The two canvas games take the whole screen.
   *
   * The app's own furniture — the streak and XP header, the tab bar, the
   * floating hymn player — costs about 315px of a 667px phone. Split what is
   * left between a drawing and a panel of answers and both end up cramped:
   * measured, To Scale had 139px of canvas on an iPhone SE. These two modes
   * are the only screens whose whole point is a picture, and each carries its
   * own way out, so the furniture steps aside while they are open.
   */
  const immersive =
    practiceMode === 'toscale' || practiceMode === 'thirdday' || practiceMode === 'twentyfaces';

  return (
    <div className="app" data-zone={tab} data-immersive={immersive ? 'true' : undefined}>
      <Ambience zone={tab} story={state.story} learning={state.learning} />

      <Header
        streak={state.streak}
        streakBroken={stats.streakBroken}
        level={level}
        xp={state.xp}
        isPremium={isPremium}
        account={account}
        audio={{ ...audio, toggle: toggleAudio }}
        onUpgrade={() => openPaywall(lockedReason())}
        onSignIn={() => setAccountPrompt({ mode: 'signin' })}
      />

      <NavBar
        current={tab}
        onSelect={selectTab}
        badges={{ saved: reviewVerses.length || undefined }}
      />

      <main className="main">
        {quizOpen ? (
          <CharacterQuiz
            onClose={closeQuiz}
            onComplete={(characterId) => {
              progress.recordCharacterQuiz(characterId);
              progress.countPlay('characterQuizTakes');
            }}
            signedIn={account.signedIn}
            isPremium={isPremium}
            takes={state.games?.characterQuizTakes ?? 0}
            onSignIn={() => setAccountPrompt({ mode: 'signup' })}
            onNeedAccount={() =>
              setAccountPrompt({
                mode: 'signup',
                reason:
                  'Your result and your streak live in this browser only. An account keeps them, on every device.',
              })
            }
            onUpgrade={() => openPaywall('The whole story — all 28 chapters — and every verse.')}
          />
        ) : (
          <>
        {/* Only when there is something to do about it: an unconfirmed account,
            and a server that can actually send the email. Showing this while
            mail is going to a log file would be asking for a link that is never
            going to arrive. */}
        {account.signedIn && !account.emailVerified && emailWorks && !confirmDismissed ? (
          <ConfirmBanner
            email={account.user.email}
            onDismiss={() => setConfirmDismissed(true)}
          />
        ) : null}
        {tab === 'home' ? (
          <HomeScreen
            onQuiz={openQuiz}
            onPlayGame={startPractice}
            trialAvailable={trialAvailable}
            trialDays={trialDays}
            verseOfDay={votd}
            streak={state.streak}
            stats={stats}
            level={level}
            xp={state.xp}
            story={state.story}
            isPremium={isPremium}
            isBookmarked={state.bookmarks.includes(votd.id)}
            onNavigate={selectTab}
            onStartDeck={startDeck}
            onToggleBookmark={progress.toggleBookmark}
            onAsk={askAbout}
            onUpgrade={() => openPaywall(lockedReason())}
          />
        ) : null}

        {tab === 'story' ? (
          openChapter ? (
            <StoryChapter
              key={openChapter}
              chapterId={openChapter}
              isBookmarked={state.bookmarks.includes(
                CHAPTERS_BY_ID[openChapter]?.verseId,
              )}
              onComplete={progress.completeChapter}
              onToggleBookmark={progress.toggleBookmark}
              onAsk={askAbout}
              onExit={() => setOpenChapter(null)}
              onNext={setOpenChapter}
            />
          ) : (
            <StoryScreen
              story={state.story}
              isPremium={isPremium}
              onOpenChapter={setOpenChapter}
              onUpgrade={() =>
                openPaywall('Read the whole story, from creation to the new creation.')
              }
            />
          )
        ) : null}

        {tab === 'flashcards' ? (
          <Flashcards
            verses={deckVerses}
            lockedCount={deckLocked}
            title={deckTitle}
            subtitle={deckSubtitle}
            learning={state.learning}
            bookmarks={state.bookmarks}
            isPremium={isPremium}
            onReveal={progress.revealVerse}
            onMark={progress.markVerse}
            onToggleBookmark={progress.toggleBookmark}
            onAsk={askAbout}
            onUpgrade={() =>
              openPaywall(`Unlock the other ${deckLocked} verses in this deck.`)
            }
            onBrowsePaths={() => selectTab('paths')}
          />
        ) : null}

        {tab === 'practice' ? (
          practiceMode === null ? (
            <PracticeScreen
              answers={state.quizAnswers}
              learning={state.learning}
              daily={state.daily}
              games={state.games}
              isPremium={isPremium}
              onStart={startPractice}
              onUpgrade={() =>
                openPaywall(`Unlock all ${QUESTIONS.length} questions in the bank.`)
              }
            />
          ) : practiceMode === 'daily' ? (
            <DailyChallenge
              daily={state.daily}
              soundOn={state.settings.sfx}
              onToggleSound={() => progress.setSetting('sfx', !state.settings.sfx)}
              onAnswer={(question, correct, xp) =>
                progress.recordAnswer(question.id, correct, xp)
              }
              onComplete={(day, correct, total, marks) => {
                progress.recordDaily(day, marks);
                handleQuizFinish(correct, total);
              }}
              onExit={() => setPracticeMode(null)}
            />
          ) : practiceMode === 'beforeafter' ? (
            <BeforeAfter
              best={state.games?.beforeAfterBest ?? 0}
              soundOn={state.settings.sfx}
              runs={state.games?.beforeAfterRuns ?? 0}
              signedIn={account.signedIn}
              onNeedAccount={() =>
                setAccountPrompt({
                  mode: 'signup',
                  reason:
                    'Your best score and your streak live in this browser only. An account keeps them, on every device.',
                })
              }
              onUpgrade={() => openPaywall('The whole story, every verse, and the full question bank.')}
              onFinish={(runScore) => {
                progress.recordGame('beforeAfterBest', runScore);
                progress.countPlay('beforeAfterRuns');
                // A long run is worth XP, but a short one should not be a
                // grind-free way to farm it — hence the square root.
                if (runScore >= 3) {
                  progress.addXp(Math.round(Math.sqrt(runScore) * 4), 'Before or After');
                }
              }}
              onExit={() => setPracticeMode(null)}
            />
          ) : practiceMode === 'thirdday' ? (
            <ThirdDay
              best={state.games?.thirdDayBest ?? 0}
              runs={state.games?.thirdDayRuns ?? 0}
              signedIn={account.signedIn}
              soundOn={state.settings.sfx}
              onNeedAccount={() =>
                setAccountPrompt({
                  mode: 'signup',
                  reason:
                    'Your best walk lives in this browser only. An account keeps it, on every device.',
                })
              }
              onUpgrade={() =>
                openPaywall('The whole story, every verse, and the full question bank.')
              }
              onFinish={(walkScore, { steps, reached }) => {
                progress.recordGame('thirdDayBest', walkScore);
                progress.countPlay('thirdDayRuns');
                // Paid per step reached, so a walk that ends early is still
                // worth something — but reaching the garden is worth more.
                if (steps > 0) {
                  progress.addXp(steps * 2 + (reached ? 6 : 0), 'The Third Day');
                }
              }}
              onExit={() => setPracticeMode(null)}
            />
          ) : practiceMode === 'twentyfaces' ? (
            <TwentyFaces
              best={state.games?.twentyFacesBest ?? 0}
              runs={state.games?.twentyFacesRuns ?? 0}
              signedIn={account.signedIn}
              soundOn={state.settings.sfx}
              onNeedAccount={() =>
                setAccountPrompt({
                  mode: 'signup',
                  reason:
                    'Your best board lives in this browser only. An account keeps it, on every device.',
                })
              }
              onUpgrade={() =>
                openPaywall('The whole story, every verse, and the full question bank.')
              }
              onFinish={(boardScore, { questions }) => {
                progress.recordGame('twentyFacesBest', boardScore);
                progress.countPlay('twentyFacesRuns');
                // Paid for solving it, with the bonus in asking less. Flat XP
                // would make a lazy twelve-question board worth the same as a
                // four-question one.
                progress.addXp(Math.max(4, 16 - questions), 'Twenty Faces');
              }}
              onExit={() => setPracticeMode(null)}
            />
          ) : practiceMode === 'toscale' ? (
            <ToScale
              best={state.games?.toScaleBest ?? 0}
              runs={state.games?.toScaleRuns ?? 0}
              signedIn={account.signedIn}
              soundOn={state.settings.sfx}
              onNeedAccount={() =>
                setAccountPrompt({
                  mode: 'signup',
                  reason:
                    'Your best score lives in this browser only. An account keeps it, on every device.',
                })
              }
              onUpgrade={() =>
                openPaywall('The whole story, every verse, and the full question bank.')
              }
              onFinish={(total) => {
                progress.recordGame('toScaleBest', total);
                progress.countPlay('toScaleRuns');
                // Worth XP, but scaled so a run of wild guesses is not a way to
                // farm it — half marks earns roughly a quiz answer's worth.
                if (total > 0) progress.addXp(Math.round(total / 25), 'To Scale');
              }}
              onExit={() => setPracticeMode(null)}
            />
          ) : practiceMode === 'recall' ? (
            <VerseRecall
              verses={deckVerses}
              onScore={(xp) => progress.addXp(xp, 'Verse recalled')}
              onExit={() => setPracticeMode(null)}
            />
          ) : (
            <PracticeRunner
              key={`${practiceMode}-${practiceRun}`}
              questions={practiceQuestions}
              lockedCount={quizLocked}
              isPremium={isPremium}
              soundOn={state.settings.sfx}
              title={PRACTICE_TITLES[practiceMode] ?? 'Practice'}
              timeLimit={practiceMode === 'timed' ? 15 : null}
              onToggleSound={() => progress.setSetting('sfx', !state.settings.sfx)}
              onAnswer={(question, correct, xp) =>
                progress.recordAnswer(question.id, correct, xp)
              }
              onFinish={handleQuizFinish}
              onExit={() => setPracticeMode(null)}
              onUpgrade={() =>
                openPaywall(`Unlock all ${QUESTIONS.length} questions in the bank.`)
              }
            />
          )
        ) : null}

        {tab === 'people' ? <CharactersScreen onAsk={askAbout} /> : null}

        {tab === 'paths' ? (
          <PathsScreen
            learning={state.learning}
            isPremium={isPremium}
            onStartDeck={startDeck}
            onUpgrade={() => openPaywall('Unlock every book-by-book learning path.')}
          />
        ) : null}

        {tab === 'saved' ? (
          <VerseListScreen
            bookmarkedVerses={bookmarkedVerses}
            reviewVerses={reviewVerses}
            bookmarks={state.bookmarks}
            learning={state.learning}
            initialMode={savedMode}
            onToggleBookmark={progress.toggleBookmark}
            onMark={progress.markVerse}
            onAsk={askAbout}
            onStartDeck={(descriptor) => {
              setSavedMode(descriptor.type);
              startDeck(descriptor);
            }}
            onNavigate={selectTab}
          />
        ) : null}

        {tab === 'chat' ? (
          <ChatScreen
            verse={chatVerse}
            onClearVerse={() => setChatVerse(null)}
            isPremium={isPremium}
            signedIn={account.signedIn}
            remaining={isPremium ? Infinity : progress.chatRemaining}
            onConsume={() => isPremium || progress.consumeChatMessage()}
            onUpgrade={() =>
              openPaywall('Ask Scripture AI as much as you like, with no daily cap.')
            }
            onNeedAccount={() =>
              setAccountPrompt({
                mode: 'signup',
                reason: 'Scripture AI is metered per account, so questions need one.',
              })
            }
          />
        ) : null}
          </>
        )}
      </main>

      <HymnDock
        hymnal={hymnal}
        music={{
          enabled: audio.enabled,
          toggle: () => {
            // An explicit "off" sticks, so the next play doesn't bring it back.
            progress.setSetting('musicBed', !audio.enabled);
            toggleAudio();
          },
          level: state.settings.musicLevel ?? 0.7,
          setLevel: (v) => progress.setSetting('musicLevel', v),
        }}
      />

      <Toasts toasts={progress.toasts} onDismiss={progress.dismissToast} />

      {paywall ? (
        <PaywallModal
          reason={paywall.reason}
          signedIn={account.signedIn}
          onClose={() => setPaywall(null)}
          onNeedAccount={() => {
            setPaywall(null);
            setAccountPrompt({
              mode: 'signup',
              reason: 'Premium is tied to an account, so it follows you between devices.',
            });
          }}
        />
      ) : null}

      {/* A reset link outranks anything else the app was about to show: the
          person arrived here to do exactly one thing. */}
      {resetToken && !resetDone ? (
        <AccountModal
          mode="reset"
          resetToken={resetToken}
          account={account}
          onClose={() => setResetDone(true)}
        />
      ) : accountPrompt ? (
        <AccountModal
          mode={accountPrompt.mode}
          reason={accountPrompt.reason}
          account={account}
          onClose={() => setAccountPrompt(null)}
          onAuthed={() => finishPrompt(accountPrompt)}
        />
      ) : null}
    </div>
  );
}
